// Мок цифровых UGC-артикулов для консоли: вкладку видно без шлюза
// (BUN_PUBLIC_MOCK_DOMAINS=ugc или запуск без BUN_PUBLIC_API_GATEWAY_URL).
// Данные выдуманные, но связные: метрики артикула сходятся с его заказами,
// сводка считается из тех же заказов и начислений.
import { sleep } from "@/lib/utils";
import type {
	UgcAdminApi,
	UgcAdminArticleDetail,
	UgcAdminArticleOrder,
	UgcAnalytics,
	UgcArticle,
	UgcArticleEvent,
	UgcChannel,
	UgcCommission,
	UgcCommissionState,
	UgcMetrics,
} from "../ugc-articles";

const DAY = 86_400_000;
const ago = (days: number, hours = 0) => new Date(Date.now() - days * DAY - hours * 3_600_000).toISOString();

type ProductSeed = { id: string; title: string; shopId: string; shopName: string; price: number };
const PRODUCTS: ProductSeed[] = [
	{ id: "prd_demo_3", title: "Льняной жакет свободного кроя", shopId: "shop-lina", shopName: "Лина", price: 8900 },
	{ id: "prd_demo_5", title: "Кожаная сумка-кроссбоди", shopId: "shop-handmade", shopName: "Мастерская Анны", price: 6400 },
	{ id: "prd_demo_2", title: "Керамическая ваза (демо)", shopId: "shop-keramika", shopName: "Гончарная мастерская", price: 3200 },
	{ id: "prd_demo_4", title: "Стеклянная лампа «Туман»", shopId: "shop-uyut", shopName: "Уют", price: 5600 },
	{ id: "prd_demo_6", title: "Кеды из замши", shopId: "shop-lina", shopName: "Лина", price: 7200 },
	{ id: "prd_demo_7", title: "Футболка оверсайз", shopId: "shop-elina", shopName: "Элина", price: 2900 },
];

type PartnerSeed = { id: string; name: string; role: "creator" | "arbitrage" };
const PARTNERS: PartnerSeed[] = [
	{ id: "ugp-anna-style", name: "Анна Стиль", role: "creator" },
	{ id: "ugp-traffic-lab", name: "Traffic Lab", role: "arbitrage" },
	{ id: "ugp-mila-home", name: "Мила про дом", role: "creator" },
	{ id: "ugp-cpa-north", name: "CPA North", role: "arbitrage" },
];

const CHANNELS: { channel: UgcChannel; placement: string; name: string }[] = [
	{ channel: "vk", placement: "VK / Сообщество бренда", name: "ВКонтакте - Осенняя кампания" },
	{ channel: "telegram", placement: "Telegram / канал автора", name: "Telegram - обзор новинок" },
	{ channel: "instagram", placement: "Instagram / рилс", name: "Рилс с примеркой" },
	{ channel: "youtube", placement: "YouTube / распаковка", name: "Распаковка и обзор" },
	{ channel: "yandex_direct", placement: "Яндекс Директ / РСЯ", name: "Yandex_Direct_Camp_1" },
	{ channel: "vk_ads", placement: "VK Реклама / Lookalike", name: "VK_Ads_Lookalike" },
	{ channel: "dzen", placement: "Дзен / статья", name: "Статья в Дзене" },
	{ channel: "offline", placement: "Офлайн / листовка в заказе", name: "Листовка в посылке" },
];

const STATUS_RU: Record<string, string> = {
	moderation: "На модерации",
	active: "Активен",
	disabled: "Отключён",
	blocked: "Заблокирован",
	deleted: "Удалён",
};

const ORDER_STATUSES = ["completed", "completed", "delivered", "in_transit", "new", "cancelled", "completed"];

const zero = (): UgcMetrics => ({
	clicks: 0,
	orders: 0,
	paidOrders: 0,
	completedOrders: 0,
	returns: 0,
	cr: 0,
	gmv: 0,
	commission: 0,
});

type Row = { article: UgcArticle; orders: UgcAdminArticleOrder[]; events: UgcArticleEvent[]; commissions: UgcCommission[] };
const rows: Row[] = [];

const commissionState = (status: string, createdDays: number): UgcCommissionState =>
	status === "cancelled" ? "cancelled" : status === "completed" && createdDays > 20 ? "available" : "hold";

const recount = (row: Row) => {
	const m = zero();
	m.clicks = row.article.metrics.clicks;
	for (const o of row.orders) {
		m.orders += 1;
		if (o.status !== "new") m.paidOrders += 1;
		if (o.status === "completed") m.completedOrders += 1;
		if (o.status === "cancelled") m.returns += 1;
		if (o.status !== "cancelled") m.gmv += o.base;
		if (o.commissionState && o.commissionState !== "cancelled") m.commission += o.commission;
	}
	m.cr = m.clicks > 0 ? Math.round((m.orders / m.clicks) * 1000) / 10 : 0;
	row.article.metrics = m;
};

let seq = 0;
const nextCode = (): string => String(70_000_000 + ((seq++ * 7_919_311 + 1_234_567) % 29_999_999));

const seed = () => {
	let n = 0;
	for (const product of PRODUCTS) {
		for (let k = 0; k < 6; k += 1) {
			n += 1;
			const ownerKind = k === 5 ? "platform" : k === 4 ? "seller" : "partner";
			const partner = ownerKind === "partner" ? PARTNERS[(n + k) % PARTNERS.length] : null;
			const ch = CHANNELS[(n * 3 + k) % CHANNELS.length]!;
			const status =
				n % 17 === 0 ? "blocked" : n % 11 === 0 ? "disabled" : n % 9 === 0 ? "moderation" : "active";
			const createdDays = 5 + ((n * 7) % 60);
			const percent = ownerKind === "partner" ? [8, 10, 12, 15][n % 4]! : 0;
			const id = `uga-${n}`;
			const code = nextCode();
			const article: UgcArticle = {
				id,
				code,
				productId: product.id,
				productTitle: product.title,
				productImage: null,
				shopId: product.shopId,
				shopName: product.shopName,
				ownerKind,
				partnerId: partner?.id ?? null,
				partnerName: partner?.name ?? null,
				partnerRole: partner?.role ?? null,
				name: ownerKind === "platform" ? "Акция OSMIX - Чёрная пятница" : ch.name,
				placement: ownerKind === "platform" ? "OSMIX / рассылка" : ch.placement,
				campaign: ownerKind === "platform" ? "Чёрная пятница 2026" : k % 2 ? "Осень 2026" : "",
				channel: ownerKind === "platform" ? "telegram" : ch.channel,
				status,
				commissionPercent: percent,
				statusReason: status === "blocked" ? "Накрутка переходов с одного IP" : status === "disabled" ? "Отключён продавцом" : null,
				metrics: { ...zero(), clicks: n % 13 === 0 ? 0 : 20 + ((n * 37) % 380) },
				createdAt: ago(createdDays),
				updatedAt: ago(Math.max(0, createdDays - 3)),
			};
			const orderCount = article.metrics.clicks === 0 ? 0 : (n * 5) % 7;
			const orders: UgcAdminArticleOrder[] = [];
			const commissions: UgcCommission[] = [];
			for (let j = 0; j < orderCount; j += 1) {
				const status = ORDER_STATUSES[(n + j) % ORDER_STATUSES.length]!;
				const days = Math.max(0, createdDays - 2 - j * 4);
				const base = product.price * (1 + ((n + j) % 2));
				const amount = Math.round((base * percent) / 100);
				const orderId = `ord-ugc-${n}-${j}`;
				const state = ownerKind === "partner" ? (j === 0 && n % 3 === 0 && status === "completed" ? "paid" : commissionState(status, days)) : null;
				const commissionId = ownerKind === "partner" ? `ugc-com-${n}-${j}` : null;
				orders.push({
					orderId,
					orderNumber: `${10_400 + n * 10 + j}`,
					status,
					base,
					commission: ownerKind === "partner" ? amount : 0,
					commissionState: state,
					commissionId,
					createdAt: ago(days, j),
				});
				if (commissionId && state) {
					commissions.push({
						id: commissionId,
						articleId: id,
						code,
						orderId,
						orderNumber: `${10_400 + n * 10 + j}`,
						partnerId: partner!.id,
						shopId: product.shopId,
						base,
						percent,
						amount,
						state,
						stateReason: state === "cancelled" ? "order_cancelled" : null,
						availableAt: state === "hold" && status === "completed" ? new Date(Date.now() + 5 * DAY).toISOString() : null,
						createdAt: ago(days, j),
						updatedAt: ago(Math.max(0, days - 1)),
					});
				}
			}
			const events: UgcArticleEvent[] = [
				{
					at: article.createdAt,
					kind: "created",
					text: `Артикул ${code} создан: ${article.name}`,
					actor: ownerKind === "platform" ? "admin@osmix.ru" : (partner?.name ?? product.shopName),
				},
				...orders.map<UgcArticleEvent>((o) => ({
					at: o.createdAt,
					kind: "order",
					text: `Заказ № ${o.orderNumber} на ${o.base.toLocaleString("ru-RU")} ₽`,
					actor: null,
				})),
			];
			if (status !== "active") {
				events.push({ at: article.updatedAt, kind: "status", text: `Статус: ${STATUS_RU[status]}${article.statusReason ? ` - ${article.statusReason}` : ""}`, actor: status === "blocked" ? "admin@osmix.ru" : null });
			}
			const row: Row = { article, orders, events, commissions };
			recount(row);
			rows.push(row);
		}
	}
};
seed();

const findRow = (id: string): Row => {
	const row = rows.find((r) => r.article.id === id);
	if (!row) throw new Error("Артикул не найден");
	return row;
};

const detail = (row: Row): UgcAdminArticleDetail => ({
	...row.article,
	orders: [...row.orders].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
	events: [...row.events].sort((a, b) => b.at.localeCompare(a.at)),
});

const inPeriod = (iso: string, from?: string, to?: string) => {
	const day = iso.slice(0, 10);
	return (!from || day >= from) && (!to || day <= to);
};

export const ugc: UgcAdminApi = {
	async list(query) {
		await sleep(120);
		const q = (query.q ?? "").trim().toLowerCase();
		const filtered = rows
			.map((r) => r.article)
			.filter(
				(a) =>
					a.status !== "deleted" &&
					(!query.shopId || a.shopId === query.shopId) &&
					(!query.partnerId || a.partnerId === query.partnerId) &&
					(!query.productId || a.productId === query.productId) &&
					(!query.status || a.status === query.status) &&
					(!query.ownerKind || a.ownerKind === query.ownerKind) &&
					(!q ||
						`${a.code} ${a.shopName} ${a.partnerName ?? ""} ${a.productTitle} ${a.name} ${a.campaign}`.toLowerCase().includes(q)),
			)
			.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
		const start = (query.page - 1) * query.pageSize;
		return { items: filtered.slice(start, start + query.pageSize), total: filtered.length };
	},
	async get(id) {
		await sleep(100);
		return detail(findRow(id));
	},
	async create(input) {
		await sleep(150);
		const product = PRODUCTS.find((p) => p.id === input.productId) ?? {
			id: input.productId,
			title: `Товар ${input.productId}`,
			shopId: "shop-unknown",
			shopName: "Магазин",
			price: 0,
		};
		const now = new Date().toISOString();
		const article: UgcArticle = {
			id: `uga-new-${Date.now()}`,
			code: nextCode(),
			productId: product.id,
			productTitle: product.title,
			productImage: null,
			shopId: product.shopId,
			shopName: product.shopName,
			ownerKind: "platform",
			partnerId: null,
			partnerName: null,
			partnerRole: null,
			name: input.name,
			placement: input.placement ?? "",
			campaign: input.campaign ?? "",
			channel: input.channel ?? "other",
			status: "active",
			commissionPercent: 0,
			statusReason: null,
			metrics: zero(),
			createdAt: now,
			updatedAt: now,
		};
		rows.unshift({ article, orders: [], commissions: [], events: [{ at: now, kind: "created", text: `Платформенный артикул ${article.code} создан: ${article.name}`, actor: "admin@osmix.ru" }] });
		return article;
	},
	async update(id, patch) {
		await sleep(120);
		const row = findRow(id);
		if (patch.status === "blocked" && !patch.reason?.trim()) throw new Error("Укажите причину блокировки");
		const now = new Date().toISOString();
		const { reason, ...fields } = patch;
		const prevStatus = row.article.status;
		Object.assign(row.article, fields, { updatedAt: now });
		if (patch.status && patch.status !== prevStatus) {
			row.article.statusReason = reason?.trim() || null;
			row.events.push({ at: now, kind: "status", text: `Статус: ${STATUS_RU[prevStatus]} → ${STATUS_RU[patch.status]}${reason ? ` - ${reason}` : ""}`, actor: "admin@osmix.ru" });
			// Блокировка за фрод аннулирует ещё не выплаченные начисления.
			if (patch.status === "blocked") {
				for (const c of row.commissions) if (c.state === "hold" || c.state === "available") Object.assign(c, { state: "cancelled", stateReason: "blocked", updatedAt: now });
				for (const o of row.orders) if (o.commissionState === "hold" || o.commissionState === "available") o.commissionState = "cancelled";
				recount(row);
			}
		} else if (Object.keys(fields).length > 0) {
			row.events.push({ at: now, kind: "edit", text: "Изменены название, площадка или кампания", actor: "admin@osmix.ru" });
		}
		return row.article;
	},
	async summary(period) {
		await sleep(140);
		const totals = zero();
		const balance = { hold: 0, available: 0, paid: 0, cancelled: 0 };
		for (const row of rows) {
			if (inPeriod(row.article.createdAt, period.from, period.to)) totals.clicks += row.article.metrics.clicks;
			for (const o of row.orders) {
				if (!inPeriod(o.createdAt, period.from, period.to)) continue;
				totals.orders += 1;
				if (o.status !== "new") totals.paidOrders += 1;
				if (o.status === "completed") totals.completedOrders += 1;
				if (o.status === "cancelled") totals.returns += 1;
				if (o.status !== "cancelled") totals.gmv += o.base;
				if (o.commissionState) {
					balance[o.commissionState] += o.commission;
					if (o.commissionState !== "cancelled") totals.commission += o.commission;
				}
			}
		}
		totals.cr = totals.clicks > 0 ? Math.round((totals.orders / totals.clicks) * 1000) / 10 : 0;
		const result: UgcAnalytics = {
			from: period.from || rows.reduce((min, r) => (r.article.createdAt < min ? r.article.createdAt : min), new Date().toISOString()).slice(0, 10),
			to: period.to || new Date().toISOString().slice(0, 10),
			groupBy: "article",
			totals,
			rows: [],
			series: [],
			balance,
		};
		return result;
	},
	async markPaid(commissionId) {
		await sleep(120);
		for (const row of rows) {
			const c = row.commissions.find((x) => x.id === commissionId);
			const o = row.orders.find((x) => x.commissionId === commissionId || x.orderId === commissionId);
			if (!c && !o) continue;
			const target = c ?? row.commissions.find((x) => x.orderId === o?.orderId);
			if (!target) break;
			if (target.state !== "available") throw new Error("Выплатить можно только комиссию в состоянии «Доступно к выплате»");
			const now = new Date().toISOString();
			Object.assign(target, { state: "paid", updatedAt: now });
			const order = row.orders.find((x) => x.orderId === target.orderId);
			if (order) order.commissionState = "paid";
			row.events.push({ at: now, kind: "commission", text: `Комиссия ${target.amount.toLocaleString("ru-RU")} ₽ по заказу № ${target.orderNumber} отмечена выплаченной`, actor: "admin@osmix.ru" });
			return target;
		}
		throw new Error("Начисление не найдено");
	},
};
